import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { getTemplatesInjectionPrompt, updateTemplatesInjectionPrompt } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

export function TemplatesInjectionPromptSection() {
    const { user } = useAuth();
    const isReviewer = user?.role === "reviewer";
    const [override, setOverride] = useState("");
    const [defaultPrompt, setDefaultPrompt] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const d = await getTemplatesInjectionPrompt();
                setOverride(d.prompt || "");
                setDefaultPrompt(d.default || "");
                setDraft(d.prompt || d.default || "");
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "Couldn't load templates prompt",
                );
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const dirty = draft.trim() !== (override || "").trim();
    const usingDefault = !override.trim();

    const save = async () => {
        setSaving(true);
        try {
            const r = await updateTemplatesInjectionPrompt(draft);
            setOverride(r.prompt || "");
            toast.success("Templates prompt saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset to the built-in default prompt?")) return;
        setSaving(true);
        try {
            await updateTemplatesInjectionPrompt("");
            setOverride("");
            setDraft(defaultPrompt);
            toast.success("Reset to default");
        } catch {
            toast.error("Couldn't reset");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="mt-8 p-4 rounded-md bg-[#0F1B3A] border border-[#2A3655]"
            data-testid="admin-templates-prompt-section"
        >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <h3 className="text-white font-bold text-lg">
                        Templates injection prompt
                    </h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-prose">
                        Appended to the AI estimate prompt only when the user
                        attaches one or more pricing templates. Use it to dial
                        in how strictly the AI follows the catalogue versus
                        falling back to market rates for missing items.
                    </p>
                </div>
                <span
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${
                        usingDefault
                            ? "bg-slate-700 text-slate-200"
                            : "bg-[#00A3FF]/20 text-[#00A3FF]"
                    }`}
                    data-testid="admin-templates-prompt-status"
                >
                    {usingDefault ? "Using default" : "Custom override"}
                </span>
            </div>
            {loading ? (
                <p className="text-xs text-slate-500 italic">Loading…</p>
            ) : (
                <>
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={10}
                        maxLength={8000}
                        spellCheck={false}
                        readOnly={isReviewer}
                        data-testid="admin-templates-prompt-textarea"
                        className="w-full px-3 py-2 text-sm bg-[#0A1128] border border-[#2A3655] rounded-sm text-white font-mono leading-relaxed focus:border-[#00A3FF] outline-none resize-y read-only:opacity-70 read-only:cursor-not-allowed"
                        placeholder={
                            isReviewer
                                ? "Read-only for reviewer accounts"
                                : "Leave blank to use built-in default"
                        }
                    />
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving || !dirty || isReviewer}
                            data-testid="admin-templates-prompt-save"
                            className="px-3 py-1.5 text-xs rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-40"
                        >
                            {saving ? "Saving…" : "Save override"}
                        </button>
                        <button
                            type="button"
                            onClick={reset}
                            disabled={saving || usingDefault || isReviewer}
                            data-testid="admin-templates-prompt-reset"
                            className="px-3 py-1.5 text-xs rounded-sm bg-[#1C253C] hover:bg-[#2A3655] text-slate-200 disabled:opacity-40"
                        >
                            Reset to default
                        </button>
                        {isReviewer && (
                            <span className="text-[10px] text-amber-300 font-bold uppercase tracking-wider">
                                Read-only for reviewer
                            </span>
                        )}
                        {dirty && !isReviewer && (
                            <span className="text-[10px] text-amber-300">
                                Unsaved changes
                            </span>
                        )}
                    </div>
                    <details className="mt-3">
                        <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-white">
                            Show built-in default prompt
                        </summary>
                        <pre
                            className="mt-2 p-2 text-[11px] bg-[#0A1128] border border-[#2A3655] rounded-sm text-slate-400 overflow-x-auto whitespace-pre-wrap"
                            data-testid="admin-templates-prompt-default"
                        >
                            {defaultPrompt}
                        </pre>
                    </details>
                </>
            )}
        </section>
    );
}


/**
 * iter-71: Superadmin-editable pricing-strategy labels + prompts.
 *
 * The 3 radio options on the Estimate dialog (backend ids `competitive`
 * / `average` / `high`) each have a user-facing label, a hint line, and
 * a prompt block that gets injected into the AI request. This tab lets
 * the superadmin rename the buttons AND rewrite the prompt text without
 * touching any code.
 *
 * The textareas display the CURRENT (overridden OR default) prompt so
 * the user can tweak incrementally. Clicking "Reset" on a card clears
 * just that override so the hardcoded default prompt comes back.
 */
